import clsx from 'clsx';
import { Plus, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/app/store';
import { Logo } from '@/components/brand/Logo';
import { Avatar } from '@/components/ui/Avatar';
import { IconButton } from '@/components/ui/IconButton';
import { useLayoutMode } from '@/platform/useLayoutMode';
import { resolveAvatarUrl } from '@/platform/avatars';
import type { SyncStatus } from '@/domain/types';
import styles from './TopBar.module.css';

function syncKey(status: SyncStatus): string {
  if (status === 'changes_pending') return 'changesPending';
  if (status === 'sync_error') return 'syncError';
  if (status === 'retrying') return 'retrying';
  return status;
}

export function TopBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const layoutMode = useLayoutMode();
  const syncStatus = useAppStore((s) => s.syncStatus);
  const online = useAppStore((s) => s.online);
  const profiles = useAppStore((s) => s.profiles);
  const session = useAppStore((s) => s.session);
  const openPlanEditor = useAppStore((s) => s.openPlanEditor);
  const setMobileTab = useAppStore((s) => s.setMobileTab);
  const syncNow = useAppStore((s) => s.syncNow);
  const refreshSyncStatus = useAppStore((s) => s.refreshSyncStatus);

  const isOffline = !online || syncStatus === 'offline';
  const syncLabel = t(`app.sync.${syncKey(syncStatus)}`);
  const showSearch = layoutMode === 'mobile';

  return (
    <header className={clsx('chrome-surface', styles.bar)}>
      <div className={styles.drag} data-tauri-drag-region>
        <Logo size="sm" className={styles.logo} />
        <button
          type="button"
          className={clsx(styles.syncBtn, isOffline ? styles.syncOffline : styles.syncSynced)}
          onClick={() => {
            if (
              syncStatus === 'sync_error' ||
              syncStatus === 'changes_pending' ||
              syncStatus === 'retrying'
            ) {
              void syncNow();
            } else {
              void refreshSyncStatus();
            }
          }}
          title={syncLabel}
        >
          <span className={styles.syncDot} aria-hidden="true" />
          <span className={styles.syncLabel}>{syncLabel}</span>
        </button>
      </div>

      <div className={styles.actions}>
        <div className={styles.avatars}>
          {profiles.slice(0, 2).map((profile) => (
            <Avatar
              key={profile.id}
              name={profile.displayName}
              color={profile.accentColor}
              size="sm"
              src={resolveAvatarUrl(profile.avatarPath, profile.updatedAt) ?? undefined}
              className={profile.id === session?.user.id ? styles.selfAvatar : undefined}
            />
          ))}
        </div>
        {showSearch && (
          <IconButton
            label={t('nav.search')}
            onClick={() => {
              setMobileTab('search');
              navigate('/app/search');
            }}
            className={styles.searchBtn}
          >
            <Search size={16} />
          </IconButton>
        )}
        <IconButton
          label={t('app.quickAdd')}
          onClick={() => openPlanEditor()}
          className={styles.addBtn}
        >
          <Plus size={16} />
        </IconButton>
      </div>
    </header>
  );
}
